import { useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Download, Copy, Share2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface GameResultCardProps {
  isWin: boolean;
  finalTotal: number;
  targetTotal: number;
  rounds: number;
  players: number;
  mvpName?: string;
  chaosAgentName?: string;
  roomCode: string;
}

export default function GameResultCard({
  isWin,
  finalTotal,
  targetTotal,
  rounds,
  players,
  mvpName,
  chaosAgentName,
  roomCode,
}: GameResultCardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { toast } = useToast();

  const generateImage = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        resolve(null);
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }

      // Set canvas size (Instagram story-friendly)
      canvas.width = 600;
      canvas.height = 800;

      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      if (isWin) {
        gradient.addColorStop(0, '#064e3b');
        gradient.addColorStop(1, '#0f172a');
      } else {
        gradient.addColorStop(0, '#7f1d1d');
        gradient.addColorStop(1, '#0f172a');
      }
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add decorative elements
      ctx.globalAlpha = 0.1;
      for (let i = 0; i < 20; i++) {
        ctx.fillStyle = isWin ? '#10b981' : '#ef4444';
        ctx.beginPath();
        ctx.arc(
          Math.random() * canvas.width,
          Math.random() * canvas.height,
          Math.random() * 50 + 10,
          0,
          Math.PI * 2
        );
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // Title emoji
      ctx.font = 'bold 80px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(isWin ? '🎉' : '💀', canvas.width / 2, 100);

      // Title
      ctx.font = 'bold 48px Arial';
      ctx.fillStyle = isWin ? '#10b981' : '#ef4444';
      ctx.fillText(isWin ? 'WE DID IT!' : 'GAME OVER', canvas.width / 2, 170);

      // Game name
      ctx.font = 'bold 32px Arial';
      ctx.fillStyle = '#10b981';
      ctx.fillText('67 CHAOS', canvas.width / 2, 220);

      // Stats box
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.roundRect(50, 260, 500, 200, 20);
      ctx.fill();

      // Stats
      ctx.fillStyle = '#94a3b8';
      ctx.font = 'bold 18px Arial';
      ctx.textAlign = 'left';
      ctx.fillText('FINAL TOTAL', 80, 310);
      ctx.fillText('TARGET', 330, 310);
      ctx.fillText('ROUNDS', 80, 400);
      ctx.fillText('PLAYERS', 330, 400);

      ctx.font = 'bold 40px Arial';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(String(finalTotal), 80, 360);
      ctx.fillStyle = '#10b981';
      ctx.fillText(String(targetTotal), 330, 360);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(String(rounds), 80, 450);
      ctx.fillText(String(players), 330, 450);

      // MVP & Chaos Agent
      let yOffset = 500;
      
      if (mvpName) {
        ctx.fillStyle = 'rgba(16, 185, 129, 0.2)';
        ctx.roundRect(50, yOffset, 500, 70, 15);
        ctx.fill();
        
        ctx.textAlign = 'center';
        ctx.font = 'bold 24px Arial';
        ctx.fillStyle = '#10b981';
        ctx.fillText(`🏆 MVP: ${mvpName}`, canvas.width / 2, yOffset + 45);
        yOffset += 90;
      }

      if (chaosAgentName) {
        ctx.fillStyle = 'rgba(239, 68, 68, 0.2)';
        ctx.roundRect(50, yOffset, 500, 70, 15);
        ctx.fill();
        
        ctx.textAlign = 'center';
        ctx.font = 'bold 24px Arial';
        ctx.fillStyle = '#ef4444';
        ctx.fillText(`💀 Chaos Agent: ${chaosAgentName}`, canvas.width / 2, yOffset + 45);
        yOffset += 90;
      }

      // Watermark
      ctx.textAlign = 'center';
      ctx.font = 'bold 20px Arial';
      ctx.fillStyle = '#64748b';
      ctx.fillText('sixsevengame.ai', canvas.width / 2, 750);
      ctx.font = '16px Arial';
      ctx.fillStyle = '#475569';
      ctx.fillText('#67chaos #SixSeven', canvas.width / 2, 780);

      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/png');
    });
  }, [isWin, finalTotal, targetTotal, rounds, players, mvpName, chaosAgentName]);

  const downloadImage = async () => {
    const blob = await generateImage();
    if (!blob) {
      toast({ title: 'Failed to generate image', variant: 'destructive' });
      return;
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `67chaos-${isWin ? 'win' : 'game-over'}-${roomCode}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({ title: 'Image downloaded! 📸' });
  };

  const copyImage = async () => {
    const blob = await generateImage();
    if (!blob) {
      toast({ title: 'Failed to generate image', variant: 'destructive' });
      return;
    }

    try {
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      toast({ title: 'Image copied to clipboard! 📋' });
    } catch {
      toast({ title: 'Copy not supported in this browser', variant: 'destructive' });
    }
  };

  const shareToTwitter = async () => {
    const text = isWin 
      ? `🎉 We hit exactly 67 in ${rounds} rounds! Play 67 CHAOS with your friends!`
      : `💀 So close! We got to ${finalTotal} trying to hit 67. Can you do better?`;
    const url = `https://sixsevengame.ai/join/${roomCode}`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}&hashtags=67chaos,SixSeven`;
    window.open(twitterUrl, '_blank');
  };

  const shareNative = async () => {
    const blob = await generateImage();
    
    if (navigator.share) {
      const shareData: ShareData = {
        title: '67 CHAOS Game Result',
        text: isWin 
          ? `🎉 We hit exactly 67! Play with us at sixsevengame.ai #67chaos`
          : `💀 We tried to hit 67 but got ${finalTotal}! Can you do better? #67chaos`,
        url: `https://sixsevengame.ai/join/${roomCode}`,
      };

      // Add file if supported
      if (blob && navigator.canShare) {
        const file = new File([blob], '67chaos-result.png', { type: 'image/png' });
        const shareWithFile = { ...shareData, files: [file] };
        if (navigator.canShare(shareWithFile)) {
          shareData.files = [file];
        }
      }

      try {
        await navigator.share(shareData);
        toast({ title: 'Shared successfully! 🚀' });
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          toast({ title: 'Share failed', variant: 'destructive' });
        }
      }
    } else {
      // Fallback to copy link
      await navigator.clipboard.writeText(`https://sixsevengame.ai/join/${roomCode}`);
      toast({ title: 'Link copied! Share it with friends!' });
    }
  };

  return (
    <Card className="glass-card p-4 space-y-4">
      <div className="text-center">
        <h3 className="font-bold text-lg text-muted-foreground mb-2">Share Your Result</h3>
      </div>

      {/* Hidden canvas for image generation */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Share buttons */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          onClick={shareNative}
          className="font-bold"
          variant="default"
        >
          <Share2 className="mr-2 h-4 w-4" />
          Share
        </Button>
        
        <Button
          onClick={shareToTwitter}
          className="font-bold bg-[#1DA1F2] hover:bg-[#1a8cd8]"
        >
          𝕏 Post
        </Button>
        
        <Button
          onClick={copyImage}
          variant="outline"
          className="font-bold"
        >
          <Copy className="mr-2 h-4 w-4" />
          Copy
        </Button>
        
        <Button
          onClick={downloadImage}
          variant="outline"
          className="font-bold"
        >
          <Download className="mr-2 h-4 w-4" />
          Save
        </Button>
      </div>
    </Card>
  );
}